import { Link } from "react-router-dom";
import { Button, Tooltip, Input, Typography, NativeSelect } from "@altimateai/lego";
import { PermissionsEnum } from "../../helpers/constants";
import DatastoreList from "./components/DatastoreList";
import { Stack } from "@altimateai/lego";
import { useMemo, useState } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import {
  Datastore,
  DBT_CLOUD_QUERY_KEYS,
  DbtCloudConnectionResponse,
  GET_INTEGRATIONS,
} from "./types";
import { useQuery } from "@tanstack/react-query";
import { listDbtCloudConnections, getIntegrations } from "@/helpers/apis";
import { Integration } from "@/types/integration";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const GET_DATASTORES = "GET-DATASTORES";

const Datastores = (): JSX.Element => {
  useBreadcrumb("Connections", { defaultParents: [{ label: "Settings", pathname: "/settings" }] });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const { datastores, loggedinUser: user } = useAppState();

  // Fetch dbt Cloud connections
  const { data: dbtCloudData } = useQuery({
    queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTIONS],
    queryFn: listDbtCloudConnections,
  });

  // Fetch dbt Core integrations
  const { data: dbtCoreData } = useQuery({
    queryKey: [GET_INTEGRATIONS],
    queryFn: getIntegrations,
  });

  const dbtCloudConnections = useMemo(
    () => dbtCloudData?.connections || [],
    [dbtCloudData?.connections]
  );

  // Filter to only dbt_core integrations (exclude dbt_cloud as they're handled separately)
  const dbtCoreIntegrations = useMemo(
    () => (dbtCoreData || []).filter((i: Integration) => i.integration_type === "dbt_core"),
    [dbtCoreData]
  );

  const addDatastorePermission = user?.permission?.includes(PermissionsEnum.DATASTORE_CREATE);

  const filteredData = datastores!.filter((datastore: Datastore) => {
    const matchesSearch = (datastore.name ?? "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesType =
      selectedType === "all" ||
      (datastore.type ?? "").toLowerCase() === selectedType.toLowerCase();
    return matchesSearch && matchesType;
  });

  // Filter dbt Cloud connections
  const filteredDbtCloudConnections = dbtCloudConnections.filter(
    (conn: DbtCloudConnectionResponse) => {
      const matchesSearch = conn.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedType === "all" || selectedType.toLowerCase() === "dbt_cloud";
      return matchesSearch && matchesType;
    }
  );

  // Filter dbt Core integrations
  const filteredDbtCoreIntegrations = dbtCoreIntegrations.filter((integration: Integration) => {
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === "all" || selectedType.toLowerCase() === "dbt_core";
    return matchesSearch && matchesType;
  });

  const options = useMemo(() => {
    const types = new Set<string>(datastores!.map(d => d.type));
    // Add dbt Cloud to type options if there are any connections
    if (dbtCloudConnections.length > 0) {
      types.add("dbt_cloud");
    }
    // Add dbt Core to type options if there are any integrations
    if (dbtCoreIntegrations.length > 0) {
      types.add("dbt_core");
    }
    return [
      { label: "All", value: "all" },
      ...Array.from(types).map(type => ({
        label: type === "dbt_cloud" ? "dbt Cloud" : type === "dbt_core" ? "dbt Core" : type,
        value: type,
      })),
    ];
  }, [datastores, dbtCloudConnections, dbtCoreIntegrations]);

  return (
    <div>
      <Stack className="al-mb-4 " justify="between" align="center">
        <Typography variant="page-heading" className="al-text-black">
          Connections
        </Typography>
        <Stack direction="row" align="center" gap={"sm"}>
          <Input
            type="search"
            placeholder="Search connections..."
            className="al-max-w-[200px]"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <NativeSelect
            defaultValue="all"
            value={selectedType}
            onChange={setSelectedType}
            options={options}
            variant={"outline"}
          />
          <Tooltip
            title={
              !addDatastorePermission ? "Insufficient permissions for adding a Connection" : ""
            }
          >
            <Button color="primary" disabled={!addDatastorePermission}>
              <Link to={"/settings/datastores/add"}>Add Connection</Link>
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
      {filteredData && (
        <DatastoreList
          datastores={{ data_stores: filteredData }}
          dbtCloudConnections={filteredDbtCloudConnections}
          dbtCoreIntegrations={filteredDbtCoreIntegrations}
        />
      )}
    </div>
  );
};

export { Datastores };
